import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormMultiSelect } from 'ui/components/elements/FormMultiSelect/FormMultiSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormTextArea } from 'ui/components/elements/FormTextArea/FormTextArea';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { useAgentProfileRegistration } from './useAgentProfileRegistration';
import IconSearch from 'ui/images/form/search.svg';
import { ToolTip } from 'ui/components/features/ToolTip/ToolTip';
import { useModal } from 'ui/components/screens/modals/useModal';
import { CompanyNameSearch } from 'ui/components/screens/modals/CompanyNameSearch/CompanyNameSearch';
import { FormCheckbox } from 'ui/components/elements/FormCheckbox/FormCheckbox';

const BgWrapper = styled.div`
    background-color: #f8f9fd;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const Wrapper = styled.div`
    margin: 0 auto;
    padding: 40px 30px;
    border-radius: 12px;
    background-color: var(--white);
    width: 600px;
    display: flex;
    flex-direction: column;
    gap: 25px;
    font-size: var(--font-medium);
    text-align: center;
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
`;

const Title = styled.div`
    font-size: var(--font-xxx-large);
    color: var(--dark-blue);
    font-weight: 600;
    margin-bottom: 5px;
    text-align: center;
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const Description = styled.div`
    font-size: 13px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

const SearchButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dark-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 30px;
    cursor: pointer;
    padding: 15px 25px;
    width: fit-content;

    img {
        margin-left: 8px;
        width: 20px;
        height: 20px;
    }
`;

const SkipButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--white);
    border: 4px double var(--dark-blue);
    color: var(--dark-blue);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 30px;
    cursor: pointer;
    padding: 15px 25px;
    width: fit-content;

    img {
        margin-left: 8px;
        width: 20px;
        height: 20px;
    }
`;

const PrivateTxt = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const SelectAllButton = styled.button`
    align-self: flex-end;
    margin-bottom: 6px;
    border: 1px solid var(--dark-blue);
    background: transparent;
    color: var(--dark-blue);
    padding: 4px 12px;
    border-radius: 14px;
    font-size: 12px;
    cursor: pointer;
    &:hover {
        background: var(--dark-blue);
        color: var(--white);
    }
`;

export const AgentProfileRegistration: React.FC = () => {
    const {
        control,
        loading,
        errors,
        areaChoices,
        employeeChoices,
        industryChoices,
        agentTypeChoices,
        showSnackbar,
        setShowSnackbar,
        submit,
        setValue,
        watch,
        onCompanySelect,
    } = useAgentProfileRegistration();
    const { openModal, closeModal, createModal } = useModal();
    const navigate = useNavigate();

    return (
        <BgWrapper>
            {createModal(
                <CompanyNameSearch onSelect={(company) => onCompanySelect(company)} closeModal={closeModal} />,
                { scrollable: false },
            )}
            <Wrapper>
                <SnackBar message="企業プロフィールを設定しました。" open={showSnackbar} setOpen={setShowSnackbar} />
                <Title>企業情報の登録</Title>
                <ToolTip tooltipText="売り手企業とのマッチングにはプロフィール設定が必要になります">
                    <SkipButton onClick={() => navigate('/agent/dashboard/')}>
                        登録をスキップしていますぐ案件を見る
                    </SkipButton>
                </ToolTip>
                <FlexContainer>
                    <FormLabel title="企業種別" isRequired />
                    {agentTypeChoices && agentTypeChoices.length > 0 && (
                        <Controller
                            name="agent_type"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={agentTypeChoices.map(({ value, label }) => ({
                                            value,
                                            label,
                                        }))}
                                    />
                                    {errors.agent_type && <FormError message={errors.agent_type.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormCheckbox
                        id="is_sole_proprietor"
                        checked={!!watch('is_sole_proprietor')}
                        onChange={() => setValue('is_sole_proprietor', !watch('is_sole_proprietor'))}
                    >
                        <Description>個人事業主の方はこちらをチェックしてください</Description>
                    </FormCheckbox>
                </FlexContainer>
                {!watch('is_sole_proprietor') && (
                    <FlexContainer>
                        <SearchButton
                            onClick={() => {
                                openModal();
                            }}
                        >
                            法人を検索する
                            <img src={IconSearch} alt="search" />
                        </SearchButton>
                    </FlexContainer>
                )}
                <FlexContainer>
                    <FormLabel title={watch('is_sole_proprietor') ? '個人事業主名（屋号）' : '企業名'} isRequired />
                    {!watch('is_sole_proprietor') && <Description>※法人を検索すると自動で入力されます</Description>}
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput
                                    {...field}
                                    hasError={!!errors.name?.message}
                                    disabled={!watch('is_sole_proprietor')}
                                />
                                {errors.name?.message && <FormError message={errors.name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                {!watch('is_sole_proprietor') && (
                    <FlexContainer>
                        <FormLabel title="法人番号" isRequired />
                        <Description>※法人を検索すると自動で入力されます</Description>
                        <Controller
                            name="company_code"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormInput {...field} hasError={!!errors.company_code?.message} disabled />
                                    {errors.company_code?.message && (
                                        <FormError message={errors.company_code.message} />
                                    )}
                                </div>
                            )}
                        />
                    </FlexContainer>
                )}
                <FlexContainer>
                    <FormLabel title="対応可能なエリア" isRequired />
                    {areaChoices && areaChoices.length > 0 && (
                        <Controller
                            name="regions_served"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormMultiSelect
                                        name="regions_served"
                                        options={areaChoices}
                                        selected={field.value ?? []}
                                        enableAutoComplete
                                        placeholder="エリアを選択"
                                        hasError={!!errors.regions_served?.message}
                                        onChange={field.onChange}
                                    />
                                    {errors.regions_served && <FormError message={errors.regions_served.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="従業員規模" isRequired />
                    {employeeChoices && employeeChoices.length > 0 && (
                        <Controller
                            name="number_of_employees"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={employeeChoices.map(([value, label]) => ({
                                            value: value,
                                            label: label,
                                        }))}
                                    />
                                    {errors.number_of_employees && (
                                        <FormError message={errors.number_of_employees.message} />
                                    )}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="対応可能な業種" isRequired />
                    {industryChoices && industryChoices.length > 0 && (
                        <Controller
                            name="industries_served"
                            control={control}
                            render={({ field }) => (
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <SelectAllButton
                                        type="button"
                                        onClick={() =>
                                            field.onChange(industryChoices.map((c) => c.value))
                                        }
                                    >
                                        全て選択
                                    </SelectAllButton>
                                    <FormMultiSelect
                                        name="industries_served"
                                        options={industryChoices}
                                        selected={field.value ?? []}
                                        enableAutoComplete
                                        placeholder="業種を選択"
                                        hasError={!!errors.industries_served?.message}
                                        onChange={field.onChange}
                                    />
                                    {errors.industries_served && (
                                        <FormError message={errors.industries_served.message} />
                                    )}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="自己PR" isRequired />
                    <Description>※企業様に公開されます。</Description>
                    <Controller
                        name="business_description"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormTextArea
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.business_description?.message}
                                />
                                {errors.business_description?.message && (
                                    <FormError message={errors.business_description.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ご請求先用 郵便番号" isRequired />
                    <Description>※マッチングするまで費用は発生しません。</Description>
                    <Controller
                        name="billing_zipcode"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.billing_zipcode?.message} />
                                {errors.billing_zipcode?.message && (
                                    <FormError message={errors.billing_zipcode.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ご請求先用 住所" isRequired />
                    <Controller
                        name="billing_address"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormTextArea
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.billing_address?.message}
                                />
                                {errors.billing_address?.message && (
                                    <FormError message={errors.billing_address.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ご請求先用 電話番号" isRequired />
                    <Description>※ハイフンなしで入力してください。</Description>
                    <Controller
                        name="billing_phone"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.billing_phone?.message} />
                                {errors.billing_phone?.message && <FormError message={errors.billing_phone.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ご請求先用 氏名" isRequired />
                    <Controller
                        name="billing_name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.billing_name?.message} />
                                {errors.billing_name?.message && <FormError message={errors.billing_name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ご請求先用 メールアドレス" isRequired />
                    <Controller
                        name="billing_email"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.billing_email?.message} />
                                {errors.billing_email?.message && <FormError message={errors.billing_email.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ホームページURL" />
                    <Controller
                        name="hp_url"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} value={field.value ?? ''} hasError={!!errors.hp_url?.message} />
                                {errors.hp_url?.message && <FormError message={errors.hp_url.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="企業画像" />
                    <ImageUpload
                        imagePath={watch('icon_image') ?? ''}
                        directory="agent"
                        fileName={'icon'}
                        fileCategory="agent"
                        disabled={loading}
                        width={180}
                        height={180}
                        onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormCheckbox
                        id="private"
                        checked={!!watch('private')}
                        onChange={() => setValue('private', !watch('private'))}
                    >
                        <PrivateTxt>オファーした相手にのみ企業情報を公開する</PrivateTxt>
                        <Description>※有効にした場合、トップページの事例にも公開されません。</Description>
                    </FormCheckbox>
                </FlexContainer>

                <ButtonContainer>
                    <ActionButton disabled={loading} onClick={submit} bgColor="dark-blue" style={{ maxWidth: '400px' }}>
                        登録する
                    </ActionButton>
                </ButtonContainer>
            </Wrapper>
        </BgWrapper>
    );
};
